import { chartTheme } from "@/pages/dealFlow/metrics/chartTheme";

/**
 * The pieces the three index-page tile decks share.
 *
 * <p>🔴 <b>Colours come from <c>chartTheme</c>, which resolves the design tokens
 * into the concrete SVG strings Recharts needs.</b> There is a second palette in
 * <c>components/portfolioAdmin/metrics/*Row.tsx</c> — raw hex including a
 * <c>#D4A017</c> that is NOT brand gold (<c>#C9A961</c> is) — and it is a
 * migration, not a precedent. A chart that copies it inherits a palette nothing
 * else on the page shares.</p>
 */

/**
 * A stage ladder's segment colours, DECLARED PER PHASE rather than derived from
 * the index.
 *
 * <p>🔴 A positional ramp reads as progress, which is right for a funnel and
 * wrong for a catalog: the buyer and submission ladders end at a win, and the
 * listing ladder ends at "closed out". Painting the last rung green because it
 * is last would tell a specialist a shelved listing was a success. So each
 * ladder says what each of its rungs means and the colour follows the meaning.</p>
 */
export type StageTone =
  | "early"
  | "working"
  | "advanced"
  | "nearly"
  | "committed"
  | "won"
  | "ended";

const STAGE_FILL: Record<StageTone, string> = {
  early: chartTheme.info,
  working: chartTheme.primaryShadow,
  advanced: chartTheme.primary,
  nearly: chartTheme.primaryHighlight,
  // Onyx for the offer rung — the chart palette's own, kept as it was when the
  // status pills moved to stage-group colours (WSC); charts carry no pills.
  committed: chartTheme.onyx,
  won: chartTheme.success,
  // Not red. Closing something out is an ordinary, correct outcome — the same
  // reasoning that paints the terminal buyer statuses `muted` rather than
  // `danger`, so that a well-worked book does not look like a wall of failures.
  ended: chartTheme.axis,
};

export function stageFill(tone: StageTone): string {
  return STAGE_FILL[tone];
}

/** One rung of a stage ladder, as the tiles render it. */
export interface StageSegment {
  key: string;
  label: string;
  count: number;
  tone: StageTone;
}

/**
 * Format a `yyyy-MM` wall-calendar key.
 *
 * <p>🔴 <b>Never through a timezone-aware formatter, and never via
 * <c>new Date(...)</c>.</b> A month bucket is a wall-calendar value, not an
 * instant: put "2026-07" through the tz formatter and every reader west of
 * Greenwich reads "Jun". This parses the literal string and indexes a table, so
 * there is no instant to shift. The same defect class has been found three times
 * in this codebase already.</p>
 */
export function monthLabel(month: string): string {
  const [year, monthPart] = month.split("-");
  const index = Number(monthPart) - 1;
  const name = MONTHS[index] ?? month;
  return `${name} ${year?.slice(2) ?? ""}`.trim();
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * A rate as a whole-number percentage, with its own denominator guard.
 *
 * <p>Returns null rather than 0 when nothing was counted — "0% converted" and
 * "nobody to convert yet" are different claims, and only one of them is a
 * problem.</p>
 */
export function ratePct(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return Math.round((numerator / denominator) * 100);
}
